import { DarkModeToggle } from "./dark-mode-toggle";
import { LanguageSwitcher } from "./language-switcher";
import { SetupPill } from "./setup-pill";
import { ActivityBell } from "./activity-bell";
import { useParams, useLocation } from "react-router-dom";
import { useUiStore } from "@/stores/ui-store";
import { useAssistantStore } from "@/stores/assistant-store";
import { useAuth } from "@/hooks/use-auth";
import { useBrands } from "@/hooks/use-brands";
import { useBrandContextOptional } from "@/contexts/brand-context";
import { useWizardStore } from "@/stores/wizard-store";
import { Menu, Search, Sparkles, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { CreateMenu } from "@/components/create/create-menu";

// Known route segments — kept for segment extraction; labels come from nav namespace
const KNOWN_ROUTE_SEGMENTS = new Set([
  "ideas", "assets", "review", "activity", "calendar", "analytics",
  "settings", "dashboard", "kit", "ai", "ai-usage", "team", "accounts",
  "notifications",
]);

function usePageLabel(pathname: string, brandSlug: string): string {
  const { t } = useTranslation("nav");
  const after = brandSlug
    ? pathname.replace(`/${brandSlug}`, "").replace(/^\//, "")
    : pathname.replace(/^\//, "");
  const segment = after.split("/")[0];
  if (KNOWN_ROUTE_SEGMENTS.has(segment)) {
    return t(segment);
  }
  return t("projects");
}

interface TopbarProps {
  /** Wizard completion 0–100. Defaults to wizard store value. */
  setupPercent?: number;
  /** Called when setup pill is clicked. Defaults to opening the wizard. */
  onOpenSetup?: () => void;
}

export function Topbar({ setupPercent, onOpenSetup }: TopbarProps) {
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const { completionPercent, openWizard } = useWizardStore();
  const location = useLocation();
  const pathname = location.pathname;
  const params = useParams<{ brandSlug: string }>();
  const { user } = useAuth();
  const brandCtx = useBrandContextOptional();
  const setPaletteOpen = useUiStore((s) => s.setPaletteOpen);
  const openAssistant = useAssistantStore((s) => s.openPanel);
  const { t } = useTranslation("nav");
  const { t: tc } = useTranslation("common");

  // Topbar renders above BrandLayout, so :brandSlug params/BrandContext may
  // not resolve here — fall back to matching the first path segment against
  // the brand list (same resolution the command palette uses).
  const { data: brands } = useBrands();
  const slugSegment = pathname.split("/")[1] ?? "";
  const currentBrand = brands?.find((b) => b.slug === slugSegment);
  const brandSlug = params.brandSlug ?? currentBrand?.slug;

  const pageLabel = usePageLabel(pathname, brandSlug ?? "");
  const userInitial = user?.name?.charAt(0)?.toUpperCase() ?? "?";

  // Prefer the actual brand name from context; fall back to the brand list / slug
  const brandName = brandCtx?.brand?.name ?? currentBrand?.name ?? brandSlug ?? "";

  const percent = setupPercent ?? completionPercent();
  const handleOpen = onOpenSetup ?? (() => openWizard(0));

  return (
    <header className="h-12 flex-shrink-0 flex items-center border-b border-border bg-background px-4 gap-3">
      {/* Mobile sidebar toggle (shown when sidebar is collapsed) */}
      {sidebarCollapsed && (
        <button
          onClick={toggleSidebar}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t("openSidebar")}
        >
          <Menu size={18} />
        </button>
      )}

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-[13px] flex-1 min-w-0" aria-label="Breadcrumb">
        {brandName && (
          <>
            <span className="text-muted-foreground truncate max-w-[120px]">{brandName}</span>
            <span className="text-border flex-shrink-0" aria-hidden="true">/</span>
          </>
        )}
        <span className="font-medium text-foreground">{pageLabel}</span>
      </nav>

      {/* Right side controls */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Primary content-creation entry — brand-scoped */}
        {currentBrand && brandSlug && (
          <CreateMenu brandSlug={brandSlug}>
            <Button size="sm" className="h-8 gap-1.5" aria-label={t("create.button")}>
              <Plus size={14} aria-hidden="true" />
              <span className="hidden sm:inline">{t("create.button")}</span>
            </Button>
          </CreateMenu>
        )}
        {/* Command palette hint (⌘K) — palette works everywhere authenticated */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="hidden sm:flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[12px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          aria-label={t("searchAriaLabel")}
        >
          <Search size={12} />
          <span>{tc("actions.search")}</span>
          <kbd className="rounded border border-border px-1 py-px text-[10px] leading-none">
            ⌘K
          </kbd>
        </button>
        {/* Pilot assistant (⌘J) — brand-scoped, panel mounts under BrandLayout */}
        {currentBrand && (
          <button
            type="button"
            onClick={() => openAssistant()}
            title={t("openPilotTitle")}
            className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[12px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={t("pilotAriaLabel")}
          >
            <Sparkles size={12} />
            <span>{t("pilot")}</span>
          </button>
        )}
        <SetupPill percent={percent} onOpen={handleOpen} />
        {/* Activity bell — brand-scoped, dot on unseen feed events */}
        {currentBrand && (
          <ActivityBell brandId={currentBrand.id} brandSlug={currentBrand.slug} />
        )}
        <LanguageSwitcher />
        <DarkModeToggle />
        {/* User avatar — initials circle */}
        <div
          role="img"
          className="w-7 h-7 rounded-full bg-brand-subtle flex items-center justify-center text-[11px] font-semibold text-brand-subtle-text flex-shrink-0"
          title={user?.name ?? tc("user")}
          aria-label={user?.name ?? tc("user")}
        >
          {userInitial}
        </div>
      </div>
    </header>
  );
}
